import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam } from '../../exam.js';
import { anyLiveJobForTypes } from '../jobs/job-store.js';

export interface ConfirmExamCreationResult {
  ok: boolean;
  /** Set on a refusal: `generating` | `nothing-to-confirm`. */
  reason?: 'generating' | 'nothing-to-confirm';
  message?: string;
  /** The exam was already confirmed (or locked by downstream work) before this call. */
  alreadyConfirmed?: boolean;
}

/**
 * The one place an exam's creation is confirmed — shared by `confirmExamCreationMutation` (the editor's
 * button) and the `confirm_exam_creation` chat tool, so both enforce the same preconditions. Confirming
 * locks the exam editor (`isExamCreatorLocked`) and releases the PDF download and online submission.
 *
 * Callers check access to the exam first; this only checks that there is a finished exam to confirm.
 */
export async function confirmExamCreation(examId: string): Promise<ConfirmExamCreationResult> {
  const examCollection = await getCollection(Exam);
  const exam: any = await examCollection.findOne(
    { _id: examId },
    {
      projection: {
        examSource: 1,
        examSourcePdfId: 1,
        'examState.examCreationConfirmed': 1,
        'examState.examCreationState.status': 1,
      } as any,
    },
  );

  const status = exam?.examState?.examCreationState?.status;
  // A job the agent just dispatched may not have written STARTED yet — confirming in that gap would lock the
  // exam under it and the job would be dropped by its own `ready()` after the user was told it is running.
  if (status === 'STARTED' || status === 'RUNNING' || (await anyLiveJobForTypes(examId, ['examCreation']))) {
    return { ok: false, reason: 'generating', message: 'The exam is still being generated.' };
  }
  if (!exam?.examSource || !exam?.examSourcePdfId) {
    return { ok: false, reason: 'nothing-to-confirm', message: 'There is no finished exam to confirm yet.' };
  }
  if (exam.examState?.examCreationConfirmed) return { ok: true, alreadyConfirmed: true };

  // `uiNeedsRefresh` makes an open client refetch the exam, so a confirm made in chat locks the editor
  // canvas and enables its download/online buttons without a manual reload.
  await examCollection.updateOne(
    { _id: examId },
    { $set: { 'examState.examCreationConfirmed': true, 'examState.uiNeedsRefresh': true } },
  );
  logger.info({ examId }, 'Exam creation confirmed — editor locked, download and online submission released');
  return { ok: true };
}
